import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'zh';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 翻译数据
const translations = {
  en: {
    // Header
    'header.title': 'Writers\' Center',
    'header.writerExchange': 'Writer Exchange',
    'header.contractPolicy': 'Contract Policy',
    'header.messages': 'Messages',
    
    // Navigation
    'nav.home': 'Home',
    'nav.workManagement': 'Work Management',
    'nav.novel': 'Novel',
    'nav.shortStory': 'Short Story',
    'nav.script': 'Script',
    'nav.interactionManagement': 'Interaction Management',
    'nav.commentManagement': 'Comment Management',
    'nav.readerCorrections': 'Reader Corrections',
    'nav.workData': 'Work Data',
    'nav.incomeManagement': 'Income Management',
    'nav.learningExchange': 'Learning & Exchange',
    'nav.writerAcademy': 'Writer Academy',
    'nav.leaveManagement': 'Leave Management',
    'nav.personalInfo': 'Personal Information',
    'nav.myContracts': 'My Contracts',
    'nav.myPosts': 'My Posts',
    
    // Main Content
    'greeting': 'Good morning',
    'greeting.afternoon': 'Good afternoon',
    'greeting.evening': 'Good evening',
    'stats.works': 'Works',
    'stats.daysJoined': 'Days Joined',
    'stats.income': 'Cumulative Income',
    'stats.wordCount': 'Cumulative Word Count',
    'works.noWorks': 'You currently have no works',
    'works.createFirst': 'Go create your first work!',
    'works.createNovel': 'Create New Novel',
    'works.more': 'More >',
    'calendar.title': 'Update Calendar',
    'calendar.rules': 'Statistical Rules',
    'calendar.applyLeave': 'Apply for Leave',
    'calendar.updated': 'Updated in',
    'calendar.today': 'Today',
    'calendar.notUpdated': 'Not Updated',
    'announcements.title': 'Official Announcements',
    'announcements.more': 'More >',
    
    // Buttons
    'btn.viewNow': 'View Now',
    'btn.create': 'Create',
    
    // Income Management Tabs
    'income.tab.authorIncome': 'Author Income',
    'income.tab.referralLinks': 'Promotion Links',
    'income.tab.settlement': 'Settlement Management',
    'income.tab.paymentAccount': 'Payment Account',
    
    // Author Income Tab
    'income.authorIncome.monthLabel': 'Month:',
    'income.authorIncome.workLabel': 'Work:',
    'income.authorIncome.allWorks': 'All Works',
    'income.authorIncome.currencyLabel': 'Currency:',
    'income.authorIncome.totalIncome': 'Total Income This Month',
    'income.authorIncome.basicIncome': 'Base Author Income',
    'income.authorIncome.readerPromoIncome': 'Reader Promotion Income',
    'income.authorIncome.authorPromoIncome': 'Author Promotion Income',
    'income.authorIncome.workSummaryTitle': 'Summary by Work',
    'income.authorIncome.table.column.work': 'Work',
    'income.authorIncome.table.column.basicIncome': 'Base Income',
    'income.authorIncome.table.column.totalIncome': 'Total Income',
    'income.authorIncome.noData': 'No data',
    'income.authorIncome.basicDetailTab': 'Base Income Details',
    'income.authorIncome.readerPromoDetailTab': 'Reader Promotion Details',
    'income.authorIncome.authorPromoDetailTab': 'Author Promotion Details',
    'income.authorIncome.loading': 'Loading...',
    'income.authorIncome.table.basicDetail.column.time': 'Time',
    'income.authorIncome.table.basicDetail.column.work': 'Work',
    'income.authorIncome.table.basicDetail.column.sourceType': 'Source Type',
    'income.authorIncome.table.basicDetail.column.reader': 'Reader',
    'income.authorIncome.table.basicDetail.column.consumption': 'Consumption Amount',
    'income.authorIncome.table.basicDetail.column.authorShare': 'Author Share',
    'income.authorIncome.table.readerPromo.column.time': 'Time',
    'income.authorIncome.table.readerPromo.column.downlineReader': 'Downline Reader',
    'income.authorIncome.table.readerPromo.column.work': 'Work',
    'income.authorIncome.table.readerPromo.column.level': 'Level',
    'income.authorIncome.table.readerPromo.column.percent': 'Share Ratio',
    'income.authorIncome.table.readerPromo.column.consumption': 'Consumption Amount',
    'income.authorIncome.table.readerPromo.column.promoIncome': 'Promotion Income',
    'income.authorIncome.table.authorPromo.column.time': 'Time',
    'income.authorIncome.table.authorPromo.column.downlineAuthor': 'Downline Author',
    'income.authorIncome.table.authorPromo.column.work': 'Work',
    'income.authorIncome.table.authorPromo.column.level': 'Level',
    'income.authorIncome.table.authorPromo.column.percent': 'Share Ratio',
    'income.authorIncome.table.authorPromo.column.baseIncome': 'Base Income',
    'income.authorIncome.table.authorPromo.column.promoIncome': 'Promotion Income',
    
    // Referral Links Tab
    'income.referralLinks.currentPlan': 'Current Promotion Plan',
    'income.referralLinks.readerPlanTitle': 'Reader Promotion Plan:',
    'income.referralLinks.exclusivePlanTag': 'Exclusive Plan',
    'income.referralLinks.readerRuleTitle': 'Reader Promotion Commission Rules:',
    'income.referralLinks.readerRuleLevel1': 'Level 1 reader consumption',
    'income.referralLinks.readerRuleLevel2': 'Level 2 reader consumption',
    'income.referralLinks.readerRuleLevel3': 'Level 3 reader consumption',
    'income.referralLinks.readerRuleLevel4': 'Level 4 reader consumption',
    'income.referralLinks.readerRuleSuffix': ' is your income',
    'income.referralLinks.authorPlanTitle': 'Author Promotion Plan:',
    'income.referralLinks.authorRuleTitle': 'Author Promotion Commission Rules:',
    'income.referralLinks.authorRuleLevel1': 'Level 1 author base income',
    'income.referralLinks.authorRuleLevel2': 'Level 2 author base income',
    'income.referralLinks.authorRuleLevel3': 'Level 3 author base income',
    'income.referralLinks.authorRuleLevel4': 'Level 4 author base income',
    'income.referralLinks.authorRuleSuffix': ' is your income',
    'income.referralLinks.promoLinkLabel': 'Promotion Link:',
    'income.referralLinks.copyLink': 'Copy Link',
    'income.referralLinks.promoCodeLabel': 'Promotion Code:',
    'income.referralLinks.copy': 'Copy',
    'income.referralLinks.qrLabel': 'QR Code:',
    'income.referralLinks.downloadPng': 'Download PNG',
    'income.referralLinks.loadingLink': 'Loading promotion link...',
    'income.referralLinks.statsTitle': 'Promotion Statistics',
    'income.referralLinks.totalRegistered': 'Total Referred Users',
    'income.referralLinks.totalPaid': 'Total Paying Users',
    'income.referralLinks.totalIncome': 'Total Promotion Income',
    'income.referralLinks.downlineListTitle': 'My Downline List',
    'income.referralLinks.table.column.userId': 'User ID',
    'income.referralLinks.table.column.username': 'Username',
    'income.referralLinks.table.column.type': 'Type',
    'income.referralLinks.table.column.registerTime': 'Register Time',
    'income.referralLinks.table.column.totalConsumption': 'Total Consumption',
    'income.referralLinks.table.column.myIncome': 'My Promotion Income',
    'income.referralLinks.noData': 'No data',
    
    // Settlement Management Tab
    'income.settlement.monthIncomeTab': 'Monthly Income',
    'income.settlement.paymentRecordTab': 'Payment Records',
    'income.settlement.currentMonthIncome': 'Total Income This Month',
    'income.settlement.currentMonthPaid': 'Paid This Month',
    'income.settlement.currentMonthUnpaid': 'Unpaid This Month',
    'income.settlement.allMonthsSummaryTitle': 'All Months Income Summary',
    'income.settlement.loading': 'Loading...',
    'income.settlement.monthIncomeTable.column.month': 'Month',
    'income.settlement.monthIncomeTable.column.totalIncome': 'Total Income',
    'income.settlement.monthIncomeTable.column.paid': 'Paid',
    'income.settlement.monthIncomeTable.column.unpaid': 'Unpaid',
    'income.settlement.monthIncomeTable.column.status': 'Status',
    'income.settlement.noData': 'No data',
    'income.settlement.status.paid': 'Paid',
    'income.settlement.status.partial': 'Partially Paid',
    'income.settlement.status.unpaid': 'Unpaid',
    'income.settlement.paymentRecordsTitle': 'Payment Records',
    'income.settlement.paymentRecordsTable.column.month': 'Month',
    'income.settlement.paymentRecordsTable.column.monthIncome': 'Monthly Income (USD)',
    'income.settlement.paymentRecordsTable.column.currency': 'Payment Currency',
    'income.settlement.paymentRecordsTable.column.amount': 'Payment Amount',
    'income.settlement.paymentRecordsTable.column.method': 'Payment Method',
    'income.settlement.paymentRecordsTable.column.payTime': 'Payment Time',
    'income.settlement.paymentRecordsTable.column.account': 'Payment Account',
    'income.settlement.paymentRecordsTable.column.txId': 'Transaction ID',
    'income.settlement.noPaymentRecords': 'No payment records',
    'income.settlement.payMethod.paypal': 'PayPal',
    'income.settlement.payMethod.alipay': 'Alipay',
    'income.settlement.payMethod.wechat': 'WeChat',
    'income.settlement.payMethod.bankTransfer': 'Bank Transfer',
    'income.settlement.payMethod.manual': 'Manual Payment',
    'income.settlement.payMethod.unknown': 'Unknown',
    'income.settlement.pagination.prev': 'Previous',
    'income.settlement.pagination.next': 'Next',
    'income.settlement.pagination.pageInfo': 'Page {page} of {total}',
    'income.settlement.detail.title': 'Payment Record Details',
    'income.settlement.detail.fields.month': 'Month:',
    'income.settlement.detail.fields.bookedAmount': 'Booked Amount (USD):',
    'income.settlement.detail.fields.paidAmount': 'Paid Amount:',
    'income.settlement.detail.fields.fxRate': 'Exchange Rate:',
    'income.settlement.detail.fields.status': 'Status:',
    'income.settlement.detail.fields.method': 'Payment Method:',
    'income.settlement.detail.fields.requestTime': 'Request Time:',
    'income.settlement.detail.fields.payTime': 'Payment Time:',
    'income.settlement.detail.fields.remark': 'Remark:',
    'income.settlement.detail.gatewayInfoTitle': 'Gateway Transaction Info:',
    'income.settlement.detail.gateway.fields.provider': 'Payment Gateway:',
    'income.settlement.detail.gateway.fields.txId': 'Third-party Transaction ID:',
    'income.settlement.detail.gateway.fields.batchId': 'Batch ID:',
    'income.settlement.detail.gateway.fields.status': 'Status:',
    'income.settlement.detail.gateway.fields.bookedAmount': 'Booked Amount (USD):',
    'income.settlement.detail.gateway.fields.paidAmount': 'Paid Amount:',
    'income.settlement.detail.gateway.fields.fxRate': 'Exchange Rate:',
    'income.settlement.detail.gateway.fields.createTime': 'Create Time:',
    'income.settlement.detail.logTitle': 'Payment Log:',
    'income.settlement.detail.log.fields.provider': 'Provider:',
    'income.settlement.detail.log.fields.txId': 'Transaction ID:',
    'income.settlement.detail.log.fields.status': 'Status:',
    
    // Payment Account Tab
    'income.paymentAccount.tipLabel': 'Tip:',
    'income.paymentAccount.tipContent': 'Please provide accurate payment information, otherwise you cannot receive royalties. It is recommended to set a default payment account.',
    'income.paymentAccount.myAccountsTitle': 'My Payment Accounts',
    'income.paymentAccount.addAccount': '+ Add Account',
    'income.paymentAccount.loading': 'Loading...',
    'income.paymentAccount.emptyTitle': 'No payment accounts added yet',
    'income.paymentAccount.emptyDesc': 'Click [Add Account] in the top right corner to start adding',
    'income.paymentAccount.defaultTag': 'Default',
    'income.paymentAccount.fields.paypalEmail': 'PayPal Email:',
    'income.paymentAccount.fields.name': 'Name:',
    'income.paymentAccount.fields.account': 'Account:',
    'income.paymentAccount.fields.wechatId': 'WeChat ID:',
    'income.paymentAccount.fields.qrCodeAlt': 'Payment QR Code',
    'income.paymentAccount.fields.bankName': 'Bank Name:',
    'income.paymentAccount.fields.accountName': 'Account Name:',
    'income.paymentAccount.fields.cardNumber': 'Card Number:',
    'income.paymentAccount.setDefault': 'Set as Default',
    'income.paymentAccount.edit': 'Edit',
    'income.paymentAccount.deleteConfirm': 'Are you sure you want to delete this payment account? This action cannot be undone.',
    'income.paymentAccount.delete': 'Delete',
    'income.paymentAccount.modal.editTitle': 'Edit Payment Account',
    'income.paymentAccount.modal.createTitle': 'Add Payment Account',
    'income.paymentAccount.modal.payMethod': 'Payment Method:',
    'income.paymentAccount.modal.label': 'Account Label:',
    'income.paymentAccount.modal.labelPlaceholder': 'e.g., My PayPal Account',
    'income.paymentAccount.modal.paypalEmail': 'PayPal Email:',
    'income.paymentAccount.modal.paypalEmailPlaceholder': 'your@email.com',
    'income.paymentAccount.modal.alipayAccount': 'Alipay Account:',
    'income.paymentAccount.modal.alipayPlaceholder': 'Phone number or email',
    'income.paymentAccount.modal.realNameOptional': 'Real Name (Optional):',
    'income.paymentAccount.modal.realNamePlaceholder': 'Real name',
    'income.paymentAccount.modal.wechatIdRequired': 'WeChat ID (Required):',
    'income.paymentAccount.modal.wechatIdPlaceholder': 'WeChat ID',
    'income.paymentAccount.modal.qrCodeUrlOptional': 'QR Code Image URL (Optional):',
    'income.paymentAccount.modal.qrCodeUrlPlaceholder': 'Enter image URL after uploading QR code',
    'income.paymentAccount.modal.wechatBindButton': 'Bind with WeChat OAuth',
    'income.paymentAccount.modal.wechatRebindButton': 'Re-bind',
    'income.paymentAccount.modal.wechatBound': 'Bound',
    'income.paymentAccount.modal.wechatBindHintPc': 'Scan with WeChat to complete authorization and binding',
    'income.paymentAccount.modal.bankName': 'Bank Name:',
    'income.paymentAccount.modal.bankNamePlaceholder': 'e.g., Industrial and Commercial Bank of China',
    'income.paymentAccount.modal.bankAccountName': 'Account Name:',
    'income.paymentAccount.modal.bankAccountNamePlaceholder': 'Account holder name',
    'income.paymentAccount.modal.bankCardNumber': 'Card Number:',
    'income.paymentAccount.modal.bankCardNumberPlaceholder': 'Bank card number',
    'income.paymentAccount.modal.setAsDefault': 'Set as default payment account',
    'income.paymentAccount.modal.updateButton': 'Update Account',
    'income.paymentAccount.modal.saveButton': 'Save Account',
    'income.paymentAccount.modal.cancel': 'Cancel',
    
    // Common
    'income.common.copySuccess': 'Copied to clipboard',
    'income.common.copyFailed': 'Copy failed',
    'income.common.validation.labelRequired': 'Please fill in account label',
    'income.common.validation.paypalEmailRequired': 'Please fill in PayPal email',
    'income.common.validation.alipayAccountRequired': 'Please fill in Alipay account',
    'income.common.validation.wechatRequired': 'Please fill in WeChat ID or QR code URL',
    'income.common.validation.wechatBindRequired': 'Please complete WeChat authorization binding first',
    'income.common.validation.bankInfoRequired': 'Please fill in complete bank card information',
    'income.common.validation.payMethodRequired': 'Please select payment method',
    'income.common.accountUpdated': 'Payment account updated',
    'income.common.accountCreated': 'Payment account created',
    'income.common.saveFailed': 'Save failed',
    'income.common.setDefaultSuccess': 'Set as default payment account',
    'income.common.setDefaultFailed': 'Set failed',
    'income.common.deleteAccountConfirm': 'Are you sure you want to delete this payment account?',
    'income.common.deleteAccountSuccess': 'Payment account deleted',
    'income.common.deleteAccountFailed': 'Delete failed',
    'income.common.levelText': 'Level {level}',
    'income.common.monthFormat': '{month}/{year}',
    'income.common.months.1': 'January',
    'income.common.months.2': 'February',
    'income.common.months.3': 'March',
    'income.common.months.4': 'April',
    'income.common.months.5': 'May',
    'income.common.months.6': 'June',
    'income.common.months.7': 'July',
    'income.common.months.8': 'August',
    'income.common.months.9': 'September',
    'income.common.months.10': 'October',
    'income.common.months.11': 'November',
    'income.common.months.12': 'December',
    'income.common.monthSelector.year': 'Year',
    'income.common.monthSelector.month': 'Month',
  },
  zh: {
    // Header
    'header.title': '作家中心',
    'header.writerExchange': '作家交流区',
    'header.contractPolicy': '签约政策',
    'header.messages': '消息',
    
    // Navigation
    'nav.home': '专区首页',
    'nav.workManagement': '作品管理',
    'nav.novel': '小说',
    'nav.shortStory': '短故事',
    'nav.script': '剧本',
    'nav.interactionManagement': '互动管理',
    'nav.commentManagement': '评论管理',
    'nav.readerCorrections': '读者纠错',
    'nav.workData': '作品数据',
    'nav.incomeManagement': '收入管理',
    'nav.learningExchange': '学习交流',
    'nav.writerAcademy': '作家学院',
    'nav.leaveManagement': '请假管理',
    'nav.personalInfo': '个人信息',
    'nav.myContracts': '我的合同',
    'nav.myPosts': '我的帖子',
    
    // Main Content
    'greeting': '上午好',
    'greeting.afternoon': '下午好',
    'greeting.evening': '晚上好',
    'stats.works': '作品数量',
    'stats.daysJoined': '入驻天数',
    'stats.income': '累计收入',
    'stats.wordCount': '累计字数',
    'works.noWorks': '你目前还没有作品',
    'works.createFirst': '快去创建第一本作品吧!',
    'works.createNovel': '新建小说',
    'works.more': '更多>',
    'calendar.title': '更新日历',
    'calendar.rules': '统计规则说明',
    'calendar.applyLeave': '申请休假',
    'calendar.updated': '已更新',
    'calendar.today': '今',
    'calendar.notUpdated': '未更新',
    'announcements.title': '官方动态',
    'announcements.more': '更多>',
    
    // Buttons
    'btn.viewNow': '立即查看',
    'btn.create': '创建',
    
    // Income Management Tabs
    'income.tab.authorIncome': '作者收入',
    'income.tab.referralLinks': '推广链接',
    'income.tab.settlement': '结算管理',
    'income.tab.paymentAccount': '收款账户',
    
    // Author Income Tab
    'income.authorIncome.monthLabel': '月份：',
    'income.authorIncome.workLabel': '作品：',
    'income.authorIncome.allWorks': '全部作品',
    'income.authorIncome.currencyLabel': '货币：',
    'income.authorIncome.totalIncome': '本月总收入',
    'income.authorIncome.basicIncome': '基础作者收入',
    'income.authorIncome.readerPromoIncome': '读者推广收入',
    'income.authorIncome.authorPromoIncome': '作者推广收入',
    'income.authorIncome.workSummaryTitle': '按作品汇总',
    'income.authorIncome.table.column.work': '作品',
    'income.authorIncome.table.column.basicIncome': '基础收入',
    'income.authorIncome.table.column.totalIncome': '总收入',
    'income.authorIncome.noData': '暂无数据',
    'income.authorIncome.basicDetailTab': '基础收入明细',
    'income.authorIncome.readerPromoDetailTab': '读者推广明细',
    'income.authorIncome.authorPromoDetailTab': '作者推广明细',
    'income.authorIncome.loading': '加载中...',
    'income.authorIncome.table.basicDetail.column.time': '时间',
    'income.authorIncome.table.basicDetail.column.work': '作品',
    'income.authorIncome.table.basicDetail.column.sourceType': '来源类型',
    'income.authorIncome.table.basicDetail.column.reader': '读者',
    'income.authorIncome.table.basicDetail.column.consumption': '消费金额',
    'income.authorIncome.table.basicDetail.column.authorShare': '作者分成',
    'income.authorIncome.table.readerPromo.column.time': '时间',
    'income.authorIncome.table.readerPromo.column.downlineReader': '下级读者',
    'income.authorIncome.table.readerPromo.column.work': '作品',
    'income.authorIncome.table.readerPromo.column.level': '层级',
    'income.authorIncome.table.readerPromo.column.percent': '分成比例',
    'income.authorIncome.table.readerPromo.column.consumption': '消费金额',
    'income.authorIncome.table.readerPromo.column.promoIncome': '推广收入',
    'income.authorIncome.table.authorPromo.column.time': '时间',
    'income.authorIncome.table.authorPromo.column.downlineAuthor': '下级作者',
    'income.authorIncome.table.authorPromo.column.work': '作品',
    'income.authorIncome.table.authorPromo.column.level': '层级',
    'income.authorIncome.table.authorPromo.column.percent': '分成比例',
    'income.authorIncome.table.authorPromo.column.baseIncome': '基础收入',
    'income.authorIncome.table.authorPromo.column.promoIncome': '推广收入',
    
    // Referral Links Tab
    'income.referralLinks.currentPlan': '当前推广方案',
    'income.referralLinks.readerPlanTitle': '读者推广方案：',
    'income.referralLinks.exclusivePlanTag': '专属方案',
    'income.referralLinks.readerRuleTitle': '推广读者的提成规则：',
    'income.referralLinks.readerRuleLevel1': '一级读者消费的 ',
    'income.referralLinks.readerRuleLevel2': '二级读者消费的 ',
    'income.referralLinks.readerRuleLevel3': '三级读者消费的 ',
    'income.referralLinks.readerRuleLevel4': '四级读者消费的 ',
    'income.referralLinks.readerRuleSuffix': ' 为您的收入',
    'income.referralLinks.authorPlanTitle': '作者推广方案：',
    'income.referralLinks.authorRuleTitle': '推广作者的提成规则：',
    'income.referralLinks.authorRuleLevel1': '一级作者基础收入的 ',
    'income.referralLinks.authorRuleLevel2': '二级作者基础收入的 ',
    'income.referralLinks.authorRuleLevel3': '三级作者基础收入的 ',
    'income.referralLinks.authorRuleLevel4': '四级作者基础收入的 ',
    'income.referralLinks.authorRuleSuffix': ' 为您的收入',
    'income.referralLinks.promoLinkLabel': '推广链接：',
    'income.referralLinks.copyLink': '复制链接',
    'income.referralLinks.promoCodeLabel': '推广码：',
    'income.referralLinks.copy': '复制',
    'income.referralLinks.qrLabel': '二维码：',
    'income.referralLinks.downloadPng': '下载PNG',
    'income.referralLinks.loadingLink': '正在加载推广链接...',
    'income.referralLinks.statsTitle': '推广统计',
    'income.referralLinks.totalRegistered': '累计推广注册用户',
    'income.referralLinks.totalPaid': '累计推广付费用户',
    'income.referralLinks.totalIncome': '累计推广总收入',
    'income.referralLinks.downlineListTitle': '我的下级列表',
    'income.referralLinks.table.column.userId': '用户ID',
    'income.referralLinks.table.column.username': '用户名',
    'income.referralLinks.table.column.type': '类型',
    'income.referralLinks.table.column.registerTime': '注册时间',
    'income.referralLinks.table.column.totalConsumption': '累计消费',
    'income.referralLinks.table.column.myIncome': '我已获得的推广收入',
    'income.referralLinks.noData': '暂无数据',
    
    // Settlement Management Tab
    'income.settlement.monthIncomeTab': '月度收入',
    'income.settlement.paymentRecordTab': '支付记录',
    'income.settlement.currentMonthIncome': '本月总收入',
    'income.settlement.currentMonthPaid': '本月已支付',
    'income.settlement.currentMonthUnpaid': '本月未支付',
    'income.settlement.allMonthsSummaryTitle': '所有月份收入汇总',
    'income.settlement.loading': '加载中...',
    'income.settlement.monthIncomeTable.column.month': '月份',
    'income.settlement.monthIncomeTable.column.totalIncome': '总收入',
    'income.settlement.monthIncomeTable.column.paid': '已支付',
    'income.settlement.monthIncomeTable.column.unpaid': '未支付',
    'income.settlement.monthIncomeTable.column.status': '状态',
    'income.settlement.noData': '暂无数据',
    'income.settlement.status.paid': '已支付',
    'income.settlement.status.partial': '部分支付',
    'income.settlement.status.unpaid': '未支付',
    'income.settlement.paymentRecordsTitle': '支付记录',
    'income.settlement.paymentRecordsTable.column.month': '月份',
    'income.settlement.paymentRecordsTable.column.monthIncome': '当月收入(USD)',
    'income.settlement.paymentRecordsTable.column.currency': '支付币种',
    'income.settlement.paymentRecordsTable.column.amount': '支付金额',
    'income.settlement.paymentRecordsTable.column.method': '支付方式',
    'income.settlement.paymentRecordsTable.column.payTime': '支付时间',
    'income.settlement.paymentRecordsTable.column.account': '收款账号',
    'income.settlement.paymentRecordsTable.column.txId': '交易单号',
    'income.settlement.noPaymentRecords': '暂无支付记录',
    'income.settlement.payMethod.paypal': 'PayPal',
    'income.settlement.payMethod.alipay': '支付宝',
    'income.settlement.payMethod.wechat': '微信',
    'income.settlement.payMethod.bankTransfer': '银行转账',
    'income.settlement.payMethod.manual': '手动支付',
    'income.settlement.payMethod.unknown': '未知',
    'income.settlement.pagination.prev': '上一页',
    'income.settlement.pagination.next': '下一页',
    'income.settlement.pagination.pageInfo': '第 {page} 页，共 {total} 页',
    'income.settlement.detail.title': '支付记录详情',
    'income.settlement.detail.fields.month': '月份:',
    'income.settlement.detail.fields.bookedAmount': '记账金额(USD):',
    'income.settlement.detail.fields.paidAmount': '实付金额:',
    'income.settlement.detail.fields.fxRate': '汇率:',
    'income.settlement.detail.fields.status': '状态:',
    'income.settlement.detail.fields.method': '收款方式:',
    'income.settlement.detail.fields.requestTime': '申请时间:',
    'income.settlement.detail.fields.payTime': '支付时间:',
    'income.settlement.detail.fields.remark': '备注:',
    'income.settlement.detail.gatewayInfoTitle': '网关交易信息:',
    'income.settlement.detail.gateway.fields.provider': '支付网关:',
    'income.settlement.detail.gateway.fields.txId': '第三方交易号:',
    'income.settlement.detail.gateway.fields.batchId': '批次号:',
    'income.settlement.detail.gateway.fields.status': '状态:',
    'income.settlement.detail.gateway.fields.bookedAmount': '记账金额(USD):',
    'income.settlement.detail.gateway.fields.paidAmount': '实付金额:',
    'income.settlement.detail.gateway.fields.fxRate': '汇率:',
    'income.settlement.detail.gateway.fields.createTime': '创建时间:',
    'income.settlement.detail.logTitle': '支付流水:',
    'income.settlement.detail.log.fields.provider': '提供商:',
    'income.settlement.detail.log.fields.txId': '交易号:',
    'income.settlement.detail.log.fields.status': '状态:',
    
    // Payment Account Tab
    'income.paymentAccount.tipLabel': '提示:',
    'income.paymentAccount.tipContent': '请提供准确的收款信息，否则无法收到稿费。建议设置一个默认收款账户。',
    'income.paymentAccount.myAccountsTitle': '我的收款账户',
    'income.paymentAccount.addAccount': '+ 新增账户',
    'income.paymentAccount.loading': '加载中...',
    'income.paymentAccount.emptyTitle': '还没有添加收款账户',
    'income.paymentAccount.emptyDesc': '点击右上角【新增账户】开始添加',
    'income.paymentAccount.defaultTag': '默认',
    'income.paymentAccount.fields.paypalEmail': 'PayPal 邮箱:',
    'income.paymentAccount.fields.name': '姓名:',
    'income.paymentAccount.fields.account': '账号:',
    'income.paymentAccount.fields.wechatId': '微信号:',
    'income.paymentAccount.fields.qrCodeAlt': '收款码',
    'income.paymentAccount.fields.bankName': '开户行:',
    'income.paymentAccount.fields.accountName': '户名:',
    'income.paymentAccount.fields.cardNumber': '卡号:',
    'income.paymentAccount.setDefault': '设为默认',
    'income.paymentAccount.edit': '编辑',
    'income.paymentAccount.deleteConfirm': '确定删除该收款账户？此操作不可恢复。',
    'income.paymentAccount.delete': '删除',
    'income.paymentAccount.modal.editTitle': '编辑收款账户',
    'income.paymentAccount.modal.createTitle': '新增收款账户',
    'income.paymentAccount.modal.payMethod': '支付方式:',
    'income.paymentAccount.modal.label': '账户标签:',
    'income.paymentAccount.modal.labelPlaceholder': '例如：我的PayPal账户',
    'income.paymentAccount.modal.paypalEmail': 'PayPal邮箱:',
    'income.paymentAccount.modal.paypalEmailPlaceholder': 'your@email.com',
    'income.paymentAccount.modal.alipayAccount': '支付宝账号:',
    'income.paymentAccount.modal.alipayPlaceholder': '手机号或邮箱',
    'income.paymentAccount.modal.realNameOptional': '真实姓名 (选填):',
    'income.paymentAccount.modal.realNamePlaceholder': '真实姓名',
    'income.paymentAccount.modal.wechatIdRequired': '微信号 (必填):',
    'income.paymentAccount.modal.wechatIdPlaceholder': '微信号',
    'income.paymentAccount.modal.qrCodeUrlOptional': '收款码图片URL (选填):',
    'income.paymentAccount.modal.qrCodeUrlPlaceholder': '上传收款码后填入图片URL',
    'income.paymentAccount.modal.wechatBindButton': '微信授权绑定',
    'income.paymentAccount.modal.wechatRebindButton': '重新绑定',
    'income.paymentAccount.modal.wechatBound': '已绑定',
    'income.paymentAccount.modal.wechatBindHintPc': '请使用微信扫码完成授权绑定',
    'income.paymentAccount.modal.bankName': '开户行:',
    'income.paymentAccount.modal.bankNamePlaceholder': '例如：中国工商银行',
    'income.paymentAccount.modal.bankAccountName': '账户名:',
    'income.paymentAccount.modal.bankAccountNamePlaceholder': '账户持有人姓名',
    'income.paymentAccount.modal.bankCardNumber': '卡号:',
    'income.paymentAccount.modal.bankCardNumberPlaceholder': '银行卡号',
    'income.paymentAccount.modal.setAsDefault': '设为默认收款账户',
    'income.paymentAccount.modal.updateButton': '更新账户',
    'income.paymentAccount.modal.saveButton': '保存账户',
    'income.paymentAccount.modal.cancel': '取消',
    
    // Common
    'income.common.copySuccess': '已复制到剪贴板',
    'income.common.copyFailed': '复制失败',
    'income.common.validation.labelRequired': '请填写账户标签',
    'income.common.validation.paypalEmailRequired': '请填写PayPal邮箱',
    'income.common.validation.alipayAccountRequired': '请填写支付宝账号',
    'income.common.validation.wechatRequired': '请填写微信号或收款码URL',
    'income.common.validation.wechatBindRequired': '请先完成微信授权绑定',
    'income.common.validation.bankInfoRequired': '请填写完整的银行卡信息',
    'income.common.validation.payMethodRequired': '请选择支付方式',
    'income.common.accountUpdated': '收款账户已更新',
    'income.common.accountCreated': '收款账户已创建',
    'income.common.saveFailed': '保存失败',
    'income.common.setDefaultSuccess': '已设置为默认收款账户',
    'income.common.setDefaultFailed': '设置失败',
    'income.common.deleteAccountConfirm': '确定要删除这个收款账户吗？',
    'income.common.deleteAccountSuccess': '收款账户已删除',
    'income.common.deleteAccountFailed': '删除失败',
    'income.common.levelText': '第{level}层',
    'income.common.monthFormat': '{year}年{month}月',
    'income.common.months.1': '1月',
    'income.common.months.2': '2月',
    'income.common.months.3': '3月',
    'income.common.months.4': '4月',
    'income.common.months.5': '5月',
    'income.common.months.6': '6月',
    'income.common.months.7': '7月',
    'income.common.months.8': '8月',
    'income.common.months.9': '9月',
    'income.common.months.10': '10月',
    'income.common.months.11': '11月',
    'income.common.months.12': '12月',
    'income.common.monthSelector.year': '年份',
    'income.common.monthSelector.month': '月份',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language') as Language;
    return saved || 'zh';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key: string, params?: Record<string, string | number>): string => {
    let text = translations[language][key as keyof typeof translations.en] || key;
    if (params) {
      Object.keys(params).forEach(param => {
        text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), String(params[param]));
      });
    }
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

